import React, { useEffect, useRef, useState, useCallback } from "react";
import maplibregl from "@/lib/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import MediaInput from "@/components/MediaInput";
import { DARK_MAP_STYLE, LANDMARK_CATEGORIES, LANDMARK_SIZES, normalizeLocationMap, applyDarkTheme, polylineMeters, formatDistance, midpointOfPolyline, createPinElement } from "@/lib/mapStyle";
import { MapPin, Route, Hand, Save, Trash2, Crosshair, Flag, Undo2, Image as ImageIcon, ChevronDown } from "lucide-react";

const ROUTE_COLORS = ["#C4A47C", "#4ea1ff", "#ff6b6b", "#51cf66", "#f7b731"];
const uid = () => Math.random().toString(36).slice(2, 10);

/**
 * LocationMapTab — Phase 6 admin editor for the Interactive Dark Location Map.
 * MapLibre GL (CARTO dark raster). Admin sets the default view, drops landmark
 * pins, and draws animated route polylines. Saved to `project.location_map`.
 */
export default function LocationMapTab({ project, pid, reload }) {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const markersRef = useRef([]);
  const modeRef = useRef("pan");
  const draftRef = useRef([]);

  const [config, setConfig] = useState(() => normalizeLocationMap(project.location_map));
  const [mode, setMode] = useState("pan");
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);
  const [expandedLm, setExpandedLm] = useState(null);

  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { draftRef.current = draft; }, [draft]);

  // ── Init map once ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: DARK_MAP_STYLE,
      center: config.center,
      zoom: config.zoom,
      pitch: config.pitch || 0,
      bearing: config.bearing || 0,
      attributionControl: true,
    });
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), "top-right");
    mapRef.current = map;

    const doResize = () => { try { map.resize(); } catch (_) {} };
    const ro = new ResizeObserver(doResize);
    try { ro.observe(containerRef.current); } catch (_) {}
    setTimeout(doResize, 80);

    map.on("load", () => {
      applyDarkTheme(map); // white roads, grey buildings, black background
      map.addSource("routes", { type: "geojson", data: routesToGeoJSON([]) });
      map.addLayer({
        id: "routes-line", type: "line", source: "routes",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": ["get", "color"], "line-width": 4, "line-opacity": 0.9 },
      });
      map.addSource("route-labels", { type: "geojson", data: labelsFC([]) });
      map.addLayer({
        id: "route-labels", type: "symbol", source: "route-labels",
        layout: { "text-field": ["get", "label"], "text-size": 12, "text-offset": [0, -0.9], "text-anchor": "bottom", "text-allow-overlap": true },
        paint: { "text-color": "#ffffff", "text-halo-color": "#000000", "text-halo-width": 1.6 },
      });
      // Committed draft segments (solid) + rubber-band to cursor (dashed).
      map.addSource("draft", { type: "geojson", data: emptyFC() });
      map.addLayer({
        id: "draft-line", type: "line", source: "draft",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": "#C4A47C", "line-width": 3.5 },
      });
      map.addSource("draft-rubber", { type: "geojson", data: emptyFC() });
      map.addLayer({
        id: "draft-rubber-line", type: "line", source: "draft-rubber",
        paint: { "line-color": "#C4A47C", "line-width": 2, "line-dasharray": [2, 1.5], "line-opacity": 0.7 },
      });
      map.addSource("draft-pts", { type: "geojson", data: emptyFC() });
      map.addLayer({
        id: "draft-pts-circle", type: "circle", source: "draft-pts",
        paint: {
          "circle-radius": 5,
          "circle-color": "#C4A47C",
          "circle-stroke-width": 2,
          "circle-stroke-color": "#000",
        },
      });
      setReady(true);
    });

    // Rubber-band: while drawing a route, show a dashed line from the last
    // committed point to the cursor so the user sees what they're about to add.
    map.on("mousemove", (e) => {
      if (modeRef.current !== "route") return;
      const d = draftRef.current;
      const src = map.getSource("draft-rubber");
      if (!src) return;
      if (!d.length) { src.setData(emptyFC()); return; }
      const last = d[d.length - 1];
      src.setData(lineFC([last, [e.lngLat.lng, e.lngLat.lat]]));
      map.getCanvas().style.cursor = "crosshair";
    });

    map.on("click", (e) => {
      const m = modeRef.current;
      const lngLat = [Number(e.lngLat.lng.toFixed(6)), Number(e.lngLat.lat.toFixed(6))];
      if (m === "landmark") {
        setConfig((c) => ({
          ...c,
          landmarks: [...c.landmarks, {
            id: uid(), name: "New landmark", lng: lngLat[0], lat: lngLat[1],
            category: "landmark", color: "#C4A47C", size: "medium", description: "",
          }],
        }));
      } else if (m === "route") {
        setDraft((d) => [...d, lngLat]);
      }
    });

    return () => { try { ro.disconnect(); } catch (_) {} try { map.remove(); } catch (_) {} mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Sync landmark markers ────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    markersRef.current.forEach((mk) => mk.remove());
    markersRef.current = config.landmarks.map((lm) => {
      const el = createPinElement(lm);
      return new maplibregl.Marker({ element: el, anchor: "bottom" })
        .setLngLat([lm.lng, lm.lat]).addTo(map);
    });
  }, [config.landmarks, ready]);

  // ── Sync route lines + distance labels ────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const src = map.getSource("routes");
    if (src) src.setData(routesToGeoJSON(config.routes));
    const lbl = map.getSource("route-labels");
    if (lbl) lbl.setData(labelsFC(config.routes));
  }, [config.routes, ready]);

  // ── Sync draft line + vertex points ───────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const line = map.getSource("draft");
    if (line) line.setData(draft.length >= 2 ? lineFC(draft) : emptyFC());
    const pts = map.getSource("draft-pts");
    if (pts) pts.setData(pointsFC(draft));
    if (!draft.length) {
      const rub = map.getSource("draft-rubber");
      if (rub) rub.setData(emptyFC());
    }
  }, [draft, ready]);

  // Clear the rubber-band + reset cursor when leaving route mode.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    if (mode !== "route") {
      const rub = map.getSource("draft-rubber");
      if (rub) rub.setData(emptyFC());
      try { map.getCanvas().style.cursor = ""; } catch (_) {}
    }
  }, [mode, ready]);

  const undoPoint = () => setDraft((d) => d.slice(0, -1));

  const saveDefaultView = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const c = map.getCenter();
    setConfig((cfg) => ({
      ...cfg,
      has_default_view: true,
      center: [Number(c.lng.toFixed(6)), Number(c.lat.toFixed(6))],
      zoom: Number(map.getZoom().toFixed(2)),
      pitch: Number(map.getPitch().toFixed(1)),
      bearing: Number(map.getBearing().toFixed(1)),
    }));
    toast.success("Default view captured");
  }, []);

  const finishRoute = () => {
    if (draft.length < 2) { toast.error("Add at least 2 points"); return; }
    const color = ROUTE_COLORS[config.routes.length % ROUTE_COLORS.length];
    setConfig((c) => ({
      ...c,
      routes: [...c.routes, {
        id: uid(), name: `Route ${c.routes.length + 1}`, color,
        animate: true, duration_ms: 3000, coords: draft,
      }],
    }));
    setDraft([]);
    setMode("pan");
    toast.success("Route added");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/projects/${pid}`, { location_map: config });
      toast.success("Location map saved");
      reload?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setSaving(false); }
  };

  const ModeBtn = ({ m, Icon, label }) => (
    <button
      onClick={() => { setMode(m); if (m !== "route") setDraft([]); }}
      data-testid={`locmap-mode-${m}`}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border transition ${mode === m ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 hover:bg-zinc-50"}`}
    >
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );

  return (
    <div className="space-y-4" data-testid="location-map-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-medium">Location Map</h2>
          <p className="text-sm text-zinc-500 mt-1">
            A cinematic dark map of the project&rsquo;s surroundings. Drop landmark pins and draw
            animated routes. Shown in the Public Viewer via the <span className="font-medium">Location</span> button.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm" data-testid="locmap-enabled-label">
          <input type="checkbox" checked={!!config.enabled}
            onChange={(e) => setConfig((c) => ({ ...c, enabled: e.target.checked }))}
            data-testid="locmap-enabled" />
          Show in viewer
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <ModeBtn m="pan" Icon={Hand} label="Pan" />
        <ModeBtn m="landmark" Icon={MapPin} label="Add landmark" />
        <ModeBtn m="route" Icon={Route} label="Draw route" />
        <button onClick={saveDefaultView} data-testid="locmap-save-view"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border border-zinc-300 hover:bg-zinc-50">
          <Crosshair className="h-3.5 w-3.5" /> Set default view
        </button>
        {mode === "route" && (
          <>
            <span className="text-xs font-medium text-[#8a6d43] bg-[#f5ede0] px-2 py-1 rounded" data-testid="locmap-draft-info">
              {draft.length} point(s){draft.length >= 2 ? ` · ${formatDistance(polylineMeters(draft))}` : ""} — click map to add
            </span>
            {draft.length > 0 && (
              <button onClick={undoPoint} data-testid="locmap-undo-point"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border border-zinc-300 hover:bg-zinc-50">
                <Undo2 className="h-3.5 w-3.5" /> Undo
              </button>
            )}
            <button onClick={finishRoute} data-testid="locmap-finish-route"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-[#C4A47C] text-zinc-900">
              <Flag className="h-3.5 w-3.5" /> Finish route
            </button>
            {draft.length > 0 && (
              <button onClick={() => setDraft([])} className="text-xs text-zinc-500 hover:text-zinc-900">Clear</button>
            )}
          </>
        )}
        <button onClick={save} disabled={saving} data-testid="locmap-save"
          className="ml-auto inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm bg-zinc-900 text-white disabled:opacity-60">
          <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div ref={containerRef} data-testid="locmap-canvas"
        className="w-full h-[460px] rounded-xl overflow-hidden ring-1 ring-zinc-200 bg-zinc-950" />

      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-zinc-200 bg-white p-3" data-testid="locmap-landmarks-list">
          <div className="text-sm font-medium mb-2 flex items-center gap-1.5"><MapPin className="h-4 w-4" /> Landmarks ({config.landmarks.length})</div>
          {config.landmarks.length === 0 && <div className="text-xs text-zinc-500 py-3">Use &ldquo;Add landmark&rdquo; then click the map.</div>}
          <div className="space-y-2 max-h-72 overflow-auto scrollbar-thin">
            {config.landmarks.map((lm, i) => (
              <div key={lm.id} className="rounded-lg border border-zinc-100 bg-zinc-50/40" data-testid={`locmap-landmark-${lm.id}`}>
                <div className="flex items-center gap-2 p-1.5">
                  <input type="color" value={lm.color || "#C4A47C"} title="Pin color"
                    onChange={(e) => updateLandmark(setConfig, i, { color: e.target.value })}
                    className="w-7 h-7 rounded cursor-pointer border border-zinc-200 shrink-0" />
                  <input value={lm.name} onChange={(e) => updateLandmark(setConfig, i, { name: e.target.value })}
                    className="flex-1 min-w-0 px-2 py-1 border border-zinc-300 rounded text-sm" data-testid={`locmap-landmark-name-${lm.id}`} />
                  <select value={lm.category || "landmark"} onChange={(e) => updateLandmark(setConfig, i, { category: e.target.value })}
                    className="px-2 py-1 border border-zinc-300 rounded text-xs">
                    {LANDMARK_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <button onClick={() => setExpandedLm(expandedLm === lm.id ? null : lm.id)}
                    className="p-1.5 hover:bg-zinc-100 rounded text-zinc-500" title="Size & logo"
                    data-testid={`locmap-landmark-opts-${lm.id}`}>
                    <ChevronDown className={`h-3.5 w-3.5 transition ${expandedLm === lm.id ? "rotate-180" : ""}`} />
                  </button>
                  <button onClick={() => flyTo(mapRef, [lm.lng, lm.lat])} className="p-1.5 hover:bg-zinc-100 rounded" title="Fly to">
                    <Crosshair className="h-3.5 w-3.5 text-zinc-500" />
                  </button>
                  <button onClick={() => setConfig((c) => ({ ...c, landmarks: c.landmarks.filter((x) => x.id !== lm.id) }))}
                    className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`locmap-landmark-del-${lm.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                {expandedLm === lm.id && (
                  <div className="px-2 pb-2.5 pt-1 space-y-2 border-t border-zinc-100">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-zinc-500 w-12">Size</span>
                      <div className="inline-flex rounded-md overflow-hidden border border-zinc-300" data-testid={`locmap-landmark-size-${lm.id}`}>
                        {LANDMARK_SIZES.map((s) => (
                          <button key={s} onClick={() => updateLandmark(setConfig, i, { size: s })}
                            data-testid={`locmap-landmark-size-${lm.id}-${s}`}
                            className={`px-2.5 py-1 text-xs capitalize transition ${(lm.size || "medium") === s ? "bg-zinc-900 text-white" : "bg-white hover:bg-zinc-50"}`}>
                            {s === "medium" ? "Med" : s === "large" ? "Big" : "Small"}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-zinc-500 mb-1 flex items-center gap-1"><ImageIcon className="h-3 w-3" /> Logo (PNG) — shows instead of the dot</div>
                      <MediaInput
                        projectId={pid}
                        value={lm.icon_url || ""}
                        onChange={(v) => updateLandmark(setConfig, i, { icon_url: v || null })}
                        accept="image/*"
                        preview="thumb"
                        kindHint="image"
                        testid={`locmap-landmark-logo-${lm.id}`}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-zinc-200 bg-white p-3" data-testid="locmap-routes-list">
          <div className="text-sm font-medium mb-2 flex items-center gap-1.5"><Route className="h-4 w-4" /> Routes ({config.routes.length})</div>
          {config.routes.length === 0 && <div className="text-xs text-zinc-500 py-3">Use &ldquo;Draw route&rdquo;, click points, then Finish.</div>}
          <div className="space-y-2 max-h-72 overflow-auto scrollbar-thin">
            {config.routes.map((r, i) => (
              <div key={r.id} className="flex items-center gap-2" data-testid={`locmap-route-${r.id}`}>
                <input type="color" value={r.color || "#C4A47C"} onChange={(e) => updateRoute(setConfig, i, { color: e.target.value })}
                  className="w-7 h-7 rounded cursor-pointer border border-zinc-200" />
                <input value={r.name} onChange={(e) => updateRoute(setConfig, i, { name: e.target.value })}
                  className="flex-1 min-w-0 px-2 py-1 border border-zinc-300 rounded text-sm" />
                <label className="text-xs text-zinc-600 flex items-center gap-1">
                  <input type="checkbox" checked={r.animate !== false} onChange={(e) => updateRoute(setConfig, i, { animate: e.target.checked })} /> anim
                </label>
                <span className="text-[10px] text-zinc-400" data-testid={`locmap-route-dist-${r.id}`}>{(r.coords || []).length} pts · {formatDistance(polylineMeters(r.coords))}</span>
                <button onClick={() => setConfig((c) => ({ ...c, routes: c.routes.filter((x) => x.id !== r.id) }))}
                  className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`locmap-route-del-${r.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function updateLandmark(setConfig, idx, patch) {
  setConfig((c) => ({ ...c, landmarks: c.landmarks.map((l, i) => (i === idx ? { ...l, ...patch } : l)) }));
}
function updateRoute(setConfig, idx, patch) {
  setConfig((c) => ({ ...c, routes: c.routes.map((r, i) => (i === idx ? { ...r, ...patch } : r)) }));
}
function flyTo(mapRef, center) { try { mapRef.current?.flyTo({ center, zoom: 15 }); } catch (_) {} }

function routesToGeoJSON(routes) {
  return {
    type: "FeatureCollection",
    features: (routes || []).filter((r) => (r.coords || []).length >= 2).map((r) => ({
      type: "Feature",
      properties: { color: r.color || "#C4A47C", id: r.id },
      geometry: { type: "LineString", coordinates: r.coords },
    })),
  };
}
function lineFC(coords) {
  return { type: "FeatureCollection", features: [{ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: coords } }] };
}
function pointsFC(coords) {
  return {
    type: "FeatureCollection",
    features: (coords || []).map((c) => ({ type: "Feature", properties: {}, geometry: { type: "Point", coordinates: c } })),
  };
}
function emptyFC() { return { type: "FeatureCollection", features: [] }; }
function labelsFC(routes) {
  return {
    type: "FeatureCollection",
    features: (routes || []).filter((r) => (r.coords || []).length >= 2).map((r) => ({
      type: "Feature",
      properties: { label: `${r.name} · ${formatDistance(polylineMeters(r.coords))}` },
      geometry: { type: "Point", coordinates: midpointOfPolyline(r.coords) },
    })),
  };
}
